<template>
  <div class="simulation-load-menu">
    <transition name="transition-fade" mode="out-in">
      <div v-if="allSimulations.length == 0" class="message">
        {{ $t("messages.noSimulations") }}
      </div>
      <button-list
        v-else
        :items="allSimulations"
        :deletable="true"
        :selected-item="currentSimulation"
        :order="order"
        :sortable="true"
        @select="onSelect"
        @update:order="updateSimulationOrder($event)"
        @delete="deleteSimulation($event)"
        @contextmenu="onContextMenu($event)"
      >
        <template #default="{ item }">
          <div>{{ item.title }}</div>
          <div class="simulation-load-menu__date mt-1">
            {{ formatDate(item.createdAt) }}
          </div>
        </template>
      </button-list>
    </transition>

    <context-menu
      v-if="contextMenu.item"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="contextMenu.item = null"
    >
      <button
        type="button"
        class="context-menu__entry"
        @click="onContextMenuDuplicate(contextMenu.item)"
      >
        {{ $t("labels.duplicateSimulation") }}
      </button>
      <button
        type="button"
        class="context-menu__entry"
        @click="onContextMenuDuplicateAndReset(contextMenu.item)"
      >
        {{ $t("labels.duplicateAndReset") }}
      </button>
      <button
        type="button"
        class="context-menu__entry"
        @click="onContextMenuExport(contextMenu.item)"
      >
        {{ $t("actions.exportSimulation") }}
      </button>
      <button
        type="button"
        class="context-menu__entry context-menu__entry--danger"
        @click="onContextMenuDelete(contextMenu.item)"
      >
        {{ $t("actions.deleteSimulation") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { formatDate as i18nFormatDate } from "@/i18n";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { showConfirm } from "@core/ui/alert/alert";

import ButtonList from "@core/ui/ButtonList.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import type { SimulationItem } from "./SimulationItem";

const { t } = useI18n();

const simulationViewStore = useSimulationViewStore();
const simulationStore = useSimulationStore();

const contextMenu = ref<{ item: SimulationItem | null; x: number; y: number }>({
  item: null,
  x: 0,
  y: 0
});

const currentSimulation = computed(() => simulationViewStore.simulation);
const allSimulations = computed(() => simulationStore.orderedItems);
const order = computed(() => [...simulationStore.order].reverse());

function onSelect(simulation: SimulationItem) {
  if (
    currentSimulation.value != null &&
    simulation.id === currentSimulation.value.id
  ) {
    simulationViewStore.loadSimulation({ simulationId: null });
  } else {
    loadSimulation(simulation);
  }
}

function loadSimulation(simulation: SimulationItem) {
  simulationViewStore.loadSimulation({ simulationId: simulation.id });
}

function updateSimulationOrder(newOrder: number[]) {
  simulationStore.saveOrder([...newOrder].reverse());
}

function onContextMenu(contextMenuData: { item: SimulationItem | null; x: number; y: number }) {
  contextMenu.value.item = contextMenuData.item;
  contextMenu.value.x = contextMenuData.x;
  contextMenu.value.y = contextMenuData.y;
}

function onContextMenuDelete(simulation: SimulationItem) {
  contextMenu.value.item = null;
  deleteSimulation(simulation);
}

async function deleteSimulation(simulation: SimulationItem) {
  if (await showConfirm(t("questions.deleteSimulation"))) {
    simulationStore.delete(simulation);
  }
}

function onContextMenuExport(simulation: SimulationItem) {
  contextMenu.value.item = null;
  simulationStore.export(simulation);
}

function onContextMenuDuplicate(simulation: SimulationItem) {
  contextMenu.value.item = null;
  simulationStore.duplicate(simulation.id);
}

function onContextMenuDuplicateAndReset(simulation: SimulationItem) {
  contextMenu.value.item = null;
  simulationStore.duplicateAndReset(simulation.id);
}

function formatDate(timeStamp: string | number): string {
  const date = new Date(timeStamp);
  return i18nFormatDate(date, "f");
}
</script>

<style lang="scss">
.simulation-load-menu {
  &__date {
    color: var(--color-text-light);
  }
}
</style>
